import React, { useState, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import EditIcon from "@mui/icons-material/Edit";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { ConditionOperators, generateId } from "../constants";
import { useTheme } from "../../../contexts/ThemeContext";

export const initialRule = {
  id: "0",
  variable: "contact.status",
  operator: "equals",
  value: "",
};

export const createInitialBranch = (name = "Branch 1") => ({
  id: generateId(),
  name,
  match: "all",
  rules: [{ ...initialRule, id: generateId() }],
});

export const normalizeConditionConfig = (dataConfig, legacyConditions) => {
  if (dataConfig?.branches && Array.isArray(dataConfig.branches)) {
    return dataConfig.branches.map((b, i) => ({
      id: b.id || generateId(),
      name: b.name || `Branch ${i + 1}`,
      match: b.match || "all",
      rules: (b.rules || []).map((r) => ({
        id: r.id || generateId(),
        variable: r.variable || "contact.status",
        operator: r.operator || "equals",
        value: r.value ?? "",
      })),
    }));
  }

  // Convert legacy format if present
  if (Array.isArray(legacyConditions) && legacyConditions.length > 0) {
    return legacyConditions.map((lc, idx) => ({
      id: generateId(),
      name: lc.type === "ELSE" ? "Else" : `Branch ${idx + 1}`,
      match: "all",
      rules: (lc.condition || []).map((c) => ({
        id: generateId(),
        variable: c.variable || "contact.status",
        operator: c.operator === "equal_to" ? "equals" : c.operator || "equals",
        value: c.value ?? "",
      })),
    }));
  }

  return [createInitialBranch("Branch 1")];
};

export const ConditionalNodeContent = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const [branches, setBranches] = useState(() =>
    normalizeConditionConfig(data?.data?.config, data?.data?.conditions)
  );

  useEffect(() => {
    if (data?.data) {
      setBranches(normalizeConditionConfig(data.data.config, data.data.conditions));
    }
  }, [data]);

  const addBranch = () => {
    setBranches((prev) => [
      ...prev,
      createInitialBranch(`Branch ${prev.length + 1}`),
    ]);
  };

  const removeBranch = (branchIndex) => {
    if (branches.length <= 1) return;
    setBranches((prev) => prev.filter((_, idx) => idx !== branchIndex));
  };

  const updateBranchMatch = (branchIndex, match) => {
    setBranches((prev) =>
      prev.map((b, idx) => (idx === branchIndex ? { ...b, match } : b))
    );
  };

  const addRule = (branchIndex) => {
    setBranches((prev) =>
      prev.map((b, idx) =>
        idx === branchIndex
          ? {
              ...b,
              rules: [
                ...b.rules,
                { id: generateId(), variable: "contact.phone", operator: "equals", value: "" },
              ],
            }
          : b
      )
    );
  };

  const removeRule = (branchIndex, ruleIndex) => {
    setBranches((prev) =>
      prev.map((b, idx) => {
        if (idx !== branchIndex) return b;
        if (b.rules.length <= 1) return b;
        return {
          ...b,
          rules: b.rules.filter((_, rIdx) => rIdx !== ruleIndex),
        };
      })
    );
  };

  const updateRule = (branchIndex, ruleIndex, field, value) => {
    setBranches((prev) =>
      prev.map((b, idx) => {
        if (idx !== branchIndex) return b;
        return {
          ...b,
          rules: b.rules.map((r, rIdx) =>
            rIdx === ruleIndex ? { ...r, [field]: value } : r
          ),
        };
      })
    );
  };

  const onSave = () => {
    const cleanBranches = branches.map((b, idx) => ({
      name: b.name || `Branch ${idx + 1}`,
      match: b.match || "all",
      rules: b.rules.map((r) => ({
        variable: r.variable.trim(),
        operator: r.operator,
        value: r.value,
      })),
    }));

    handleClose(
      {
        config: { branches: cleanBranches },
        conditions: branches, // keep for backward compat
      },
      isEdit
    );
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 650,
    maxWidth: "95vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-pink-500/10 text-pink-500">
            <CallSplitIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit Condition Branches" : "Configure Condition (If / Else)"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Branch contact journeys based on context variables, call results, or contact fields.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          {branches.map((branch, bIdx) => (
            <div
              key={branch.id || bIdx}
              className={`p-3.5 rounded-xl border ${
                darkMode ? "border-zinc-700 bg-zinc-900/60" : "border-slate-200 bg-slate-50"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-pink-500/20 text-pink-600 dark:text-pink-300">
                    Branch {bIdx + 1}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
                      Match:
                    </span>
                    <select
                      value={branch.match}
                      onChange={(e) => updateBranchMatch(bIdx, e.target.value)}
                      className={`text-xs px-2 py-1 rounded border ${
                        darkMode
                          ? "bg-zinc-800 border-zinc-700 text-zinc-200"
                          : "bg-white border-slate-300 text-slate-700"
                      }`}
                    >
                      <option value="all">ALL Rules (AND)</option>
                      <option value="any">ANY Rule (OR)</option>
                    </select>
                  </div>
                </div>

                {branches.length > 1 && (
                  <IconButton size="small" onClick={() => removeBranch(bIdx)} color="error">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </div>

              <div className="space-y-2">
                {branch.rules.map((rule, rIdx) => (
                  <div key={rule.id || rIdx} className="flex items-center gap-2">
                    <TextField
                      size="small"
                      label="Variable"
                      value={rule.variable}
                      onChange={(e) => updateRule(bIdx, rIdx, "variable", e.target.value)}
                      placeholder="contact.status"
                      style={{ flex: 1.2 }}
                    />
                    <FormControl size="small" style={{ flex: 1 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={rule.operator}
                        label="Operator"
                        onChange={(e) => updateRule(bIdx, rIdx, "operator", e.target.value)}
                      >
                        {ConditionOperators.map((op) => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!["is_empty", "is_not_empty", "is_true", "is_false"].includes(rule.operator) && (
                      <TextField
                        size="small"
                        label="Expected Value"
                        value={rule.value}
                        onChange={(e) => updateRule(bIdx, rIdx, "value", e.target.value)}
                        placeholder="interested"
                        style={{ flex: 1 }}
                      />
                    )}
                    {branch.rules.length > 1 && (
                      <IconButton size="small" onClick={() => removeRule(bIdx, rIdx)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                ))}
              </div>

              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => addRule(bIdx)}
                style={{ marginTop: 8, fontSize: 11 }}
              >
                Add Rule
              </Button>
            </div>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addBranch}
            fullWidth
            style={{ textTransform: "none", fontSize: 13 }}
          >
            Add Another Branch (Else If)
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            style={{ backgroundColor: "#db2777" }}
          >
            {isEdit ? "Update Condition" : "Add Condition"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const ConditionalNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const branches = cfg.branches || normalizeConditionConfig(data?.config, data?.conditions);

  const isBranchConnected = (branchKey) =>
    edges.some((e) => e.source === rest.id && e.sourceHandle === branchKey);

  return (
    <div
      className={`w-64 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-pink-500 shadow-pink-500/20" : "hover:border-pink-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <CallSplitIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            Condition (If / Else)
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Conditions">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2.5 space-y-2">
        {branches.map((branch, idx) => {
          const branchKey = `branch_${idx}`;
          const connected = isBranchConnected(branchKey);
          const firstRule = branch.rules?.[0];
          const summary = firstRule
            ? `${firstRule.variable} ${firstRule.operator} ${firstRule.value || ""}`
            : "No rules";

          return (
            <div
              key={branchKey}
              className={`relative flex items-center justify-between p-1.5 rounded-lg border ${
                darkMode ? "bg-zinc-900 border-zinc-800" : "bg-slate-50 border-slate-200"
              }`}
            >
              <div className="min-w-0 pr-6">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
                  <span className={`text-[11px] font-bold ${darkMode ? "text-pink-400" : "text-pink-600"}`}>
                    Branch {idx + 1}
                  </span>
                  <span className={`text-[9px] uppercase font-semibold text-slate-400`}>
                    ({branch.match || "all"})
                  </span>
                </div>
                <div className={`text-[10px] truncate mt-0.5 ${darkMode ? "text-zinc-400" : "text-slate-600"}`}>
                  {summary}
                </div>
              </div>

              <HandleDot
                addVisible={!connected}
                id={branchKey}
                type="source"
                position={Position.Right}
                onAdd={(v) =>
                  onAdd(v, rest, rest.id, {
                    type: "CONDITION",
                    key: branchKey,
                    branchKey,
                    branchIndex: idx,
                  })
                }
              />
            </div>
          );
        })}

        {/* Else branch */}
        <div
          className={`relative flex items-center justify-between p-1.5 rounded-lg border ${
            darkMode ? "bg-zinc-900 border-zinc-800" : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className="min-w-0 pr-6">
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              <span className={`text-[11px] font-bold ${darkMode ? "text-zinc-300" : "text-slate-700"}`}>
                Else (Default)
              </span>
            </div>
            <div className={`text-[10px] truncate mt-0.5 ${darkMode ? "text-zinc-500" : "text-slate-400"}`}>
              Fallback if no branches match
            </div>
          </div>

          <HandleDot
            addVisible={!isBranchConnected("else")}
            id="else"
            type="source"
            position={Position.Right}
            onAdd={(v) =>
              onAdd(v, rest, rest.id, {
                type: "CONDITION",
                key: "else",
                branchKey: "else",
                branchIndex: branches.length,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};
